import type {
  NotificationRule,
  NotificationRuleCategory,
} from '@eppendorf/vnls-notifications-utils';
import { useTranslation } from 'react-i18next';

import { getDeviceEventCategoryLabel } from '$shared/i18n/device-event-category-label';

import {
  type DiffResult,
  ObjectDiffView,
} from '$features/audit-events/components/diff-view/diff-view';

type NotificationRuleSnapshot = Partial<NotificationRule>;

function groupCategories(categories: NotificationRuleCategory[]) {
  const grouped = new Map<string, Set<string>>();

  categories.forEach(({ key, subCategories }) => {
    const selected = grouped.get(key) ?? new Set<string>();
    subCategories.forEach((subCategory) => selected.add(subCategory));
    grouped.set(key, selected);
  });

  return grouped;
}

export function NotiRuleDiffDisplay({
  before,
  after,
}: {
  before?: NotificationRuleSnapshot;
  after?: NotificationRuleSnapshot;
}) {
  const { t } = useTranslation();

  if (!before || !after) {
    return <ObjectDiffView lhs={before} rhs={after} />;
  }

  const {
    categories: beforeCategories = [],
    tags: beforeTags,
    ...beforeSettings
  } = before;
  const { categories: afterCategories = [], tags: afterTags, ...afterSettings } = after;
  const previous = groupCategories(beforeCategories);
  const current = groupCategories(afterCategories);
  const categoryKeys = new Set([...previous.keys(), ...current.keys()]);
  const changes: Pick<DiffResult, 'added' | 'deleted'> = { added: [], deleted: [] };
  const previousSeverities = new Set(beforeTags ?? []);
  const currentSeverities = new Set(afterTags ?? []);
  const addedSeverities = [...currentSeverities].filter(
    (severity) => !previousSeverities.has(severity),
  );
  const deletedSeverities = [...previousSeverities].filter(
    (severity) => !currentSeverities.has(severity),
  );
  const hasSeverityChanges = addedSeverities.length > 0 || deletedSeverities.length > 0;
  const severityPath = [t('auditEvents.detailsLabels.severity')];

  if (addedSeverities.length) {
    changes.added.push({ path: severityPath, value: addedSeverities });
  }
  if (deletedSeverities.length) {
    changes.deleted.push({ path: severityPath, value: deletedSeverities });
  }

  const categoryLabel = (key: string) => getDeviceEventCategoryLabel(key, t);

  categoryKeys.forEach((key) => {
    const oldSelections = previous.get(key) ?? new Set<string>();
    const newSelections = current.get(key) ?? new Set<string>();
    const added = [...newSelections].filter((selection) => !oldSelections.has(selection));
    const deleted = [...oldSelections].filter(
      (selection) => !newSelections.has(selection),
    );
    const path = hasSeverityChanges
      ? [t('auditEvents.detailsLabels.categories'), categoryLabel(key)]
      : [categoryLabel(key)];

    if (added.length) {
      changes.added.push({
        path,
        pathLabel: path.join(' → '),
        value: added.map(categoryLabel),
      });
    }
    if (deleted.length) {
      changes.deleted.push({
        path,
        pathLabel: path.join(' → '),
        value: deleted.map(categoryLabel),
      });
    }
  });

  return (
    <ObjectDiffView
      lhs={beforeSettings}
      rhs={afterSettings}
      additionalChanges={changes}
      additionalChangesColumnLabel={hasSeverityChanges ? undefined : 'Category'}
    />
  );
}
